<template>
  <VLoader v-if="loading" />
  <div v-else class="pretraining-quiz">
    <h2 class="pretraining-quiz-heading lg:text-center">{{ title }}</h2>
    <div class="pretraining-quiz-quizzes">
      <div
        v-for="({ id: questionId, question, answers }, index) in questions"
        :key="questionId"
        class="py-4 sm:py-8"
        :class="index === questions.length - 1 ? 'border-b-0' : 'border-b'"
      >
        <h4 class="pretraining-quiz-question">{{ question }}</h4>
        <ul>
          <li v-for="{ id: answerId, answer } in answers.data" :key="answerId" class="mb-4">
            <input
              :value="answerId"
              :id="answerId"
              type="radio"
              v-model="mappedAnswersToQuestionId[questionId]"
              class="mr-4"
            />
            <label :for="answerId">{{ answer }}</label>
          </li>
        </ul>
      </div>
    </div>
    <button
      class="py-5 mx-auto my-10 rounded-full button"
      :disabled="!canSubmitQuiz"
      @click.prevent="submitQuiz"
    >
      Submit Quiz
    </button>

    <!-- modal shows on fail quiz submission -->
    <Modal
      :show="showFailModal"
      @close="showFailModal = false"
      :stopEvent="true"
      width="95%"
      maxWidth="800px"
      theme="dark"
    >
      <div class="pretraining-quiz-modal">
        <div class="pretraining-quiz-modal-content">
          <h2 class="pretraining-quiz-modal-heading">
            {{ `You got ${passedQuestionsCount}/${totalQuestions}` }}
          </h2>
          <h3 class="pretraining-quiz-modal-subheading">Here's what you missed</h3>
          <ul class="pretraining-quiz-modal-list">
            <li
              v-for="(failed, index) in failedQuestions"
              :key="`failed-${index}`"
              class="pretraining-quiz-modal-list-item"
            >
              <VSvgIcon name="close" class="flex-shrink-0" /><span
                class="pretraining-quiz-modal-list-item-text"
                >{{ failed }}</span
              >
            </li>
          </ul>
          <div class="pretraining-quiz-modal-buttons">
            <button class="button pretraining-quiz-modal-buttons-retake" @click="resetQuiz">
              Re-take Quiz
            </button>
            <button class="pretraining-quiz-modal-buttons-rewatch" @click="$emit('fail')">
              Re-watch Video
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- modal shows on success quiz submission -->
    <Modal
      :show="showSuccessModal"
      @close="showSuccessModal = false"
      :stopEvent="true"
      width="95%"
      maxWidth="800px"
      theme="dark"
    >
      <div class="py-10 pretraining-quiz-modal">
        <div class="pretraining-quiz-modal-content">
          <h2 class="mb-8 pretraining-quiz-modal-heading">
            {{ `You got ${totalQuestions}/${totalQuestions}` }}
          </h2>
          <h3 class="mb-16 text-center text-ricoma-gray-700">Great Job!</h3>
          <div class="pretraining-quiz-modal-buttons">
            <button
              class="button pretraining-quiz-modal-buttons-retake"
              @click="proceedToNextVideo"
            >
              {{ isLast ? 'schedule new training' : 'continue to next lesson' }}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import Modal from '@/components/shared/modal/Modal';
import VLoader from '@/components/base/VLoader';
import VSvgIcon from '@/components/base/VSvgIcon';

export default {
  name: 'PretrainingLessonQuiz',
  components: { Modal, VLoader, VSvgIcon },
  props: { quiz: Object, title: String, isLast: Boolean },
  data() {
    return {
      loading: false,
      showFailModal: false,
      showSuccessModal: false,
      questions: [],
      mappedAnswersToQuestionId: {},
      failedQuestions: [],
      missingQuestions: [],
    };
  },
  computed: {
    mappedQuestionToItsId() {
      return this.questions.reduce((acc, q) => ((acc[q.id] = q.question), acc), {});
    },
    passedQuestionsCount() {
      return !!this.failedQuestions.length && this.totalQuestions - this.failedQuestions.length;
    },
    totalQuestions() {
      return this.questions.length;
    },
    canSubmitQuiz() {
      return Object.values(this.mappedAnswersToQuestionId).every(answer => !!answer);
    },
  },
  methods: {
    mapAnswersToQuestionId(questions) {
      this.mappedAnswersToQuestionId = questions
        .map(q => q.id)
        .reduce((acc, next) => ((acc[next] = null), acc), {});
    },
    resetQuiz() {
      this.mapAnswersToQuestionId(this.questions);
      this.failedQuestions = [];
      this.missingQuestions = [];
      this.showFailModal = false;
    },
    proceedToNextVideo() {
      this.$emit('success');
      this.showSuccessModal = false;
    },
    async submitQuiz() {
      this.loading = true;
      const { false: failedQuestions, result: passedQuiz } = await this.$store.dispatch(
        'customer/submitQuiz',
        {
          quizId: this.quiz.id,
          answers: this.mappedAnswersToQuestionId,
        },
      );
      this.loading = false;

      this.failedQuestions = failedQuestions.map(
        questionId => this.mappedQuestionToItsId[questionId],
      );

      // emit success event if true, else prompt user with options
      passedQuiz ? (this.showSuccessModal = true) : (this.showFailModal = true);
    },
  },
  created() {
    this.questions = this.quiz.questions.data;
    if (this.questions.length === 0) {
      this.$emit('success');
    } else {
      this.mapAnswersToQuestionId(this.quiz.questions.data);
    }
  },
};
</script>
